import * as React from 'react';
import { useLazyQuery } from '@apollo/client';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Icon,
  Modal,
  ModalProps,
  Spinner,
  Text,
  useToast,
} from '@quality24/design-system';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';

import { JsonHospitalConfig } from '@quality24/inpatient-typings';
import {
  ActiveOrderAction,
  getOrderActionTranslation,
} from '../../utils/patientCallUtils';

import {
  InputModes,
  QueryByIdResult,
  QueryByRegistryResult,
  QueryEmployee,
} from './types';
import { GET_EMPLOYEE_BY_ID, GET_EMPLOYEE_BY_REGISTRY } from './graphql';
import SelectionHandler from './components/SelectionHandler';
import { AuthUserRole } from '../../@types/auth';

export interface Props extends Omit<ModalProps, 'id'> {
  id?: string;
  user: {
    role: AuthUserRole;
    hospitalId?: string;
    employeeId?: string;
    email?: string;
    modules?: string[];
  };
  hospitalConfig?: JsonHospitalConfig;
  mode?: InputModes;
  actionLabel: ActiveOrderAction;
  submitButtonLabel?: string;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (selected: string | null) => Promise<void>;
  skipIdentification?: boolean;
  employeeId?: QueryEmployee['id'];
}

/**
 *
 * Useful if we need to render the whole Modal unlike EmployeeForm. This component might probably be replaced by OrderSolutionContainer in the future.
 */
const EmployeeIdContainer: React.FunctionComponent<Props> = ({
  user,
  hospitalConfig,
  mode = 'input',
  onHide,
  actionLabel,
  submitButtonLabel,
  loading: saving,
  onCancel,
  onSubmit,
  id,
  skipIdentification,
  employeeId,
  ...modalProps
}) => {
  const [labelInfinitive, labelPast] = getOrderActionTranslation(actionLabel);
  const { error } = useToast();

  const canSkipIdentification =
    skipIdentification || (user.employeeId && actionLabel === 'Iniciar');

  // The employeeRegistry might be an employee.registry.hospitalRegistry or an employee.id depending on the resolution mode
  const [employeeRegistry, setEmployeeRegistry] = React.useState<string>();
  const [selectedEmployee, setSelectedEmployee] =
    React.useState<QueryEmployee>();
  const [fallbackMode, setFallbackMode] = React.useState<InputModes>();
  const [submited, setSubmited] = React.useState(false);

  const isInvalid =
    !!hospitalConfig?.orderSolutionKey?.required &&
    submited &&
    (!selectedEmployee || !selectedEmployee.enabled);

  const [getEmployeesByRegistry, { loading: searchingRegistry }] =
    useLazyQuery<QueryByRegistryResult>(GET_EMPLOYEE_BY_REGISTRY, {
      variables: { registry: '' },
      onCompleted: (data) => {
        setSelectedEmployee(data.employees.nodes[0]);
        setSubmited(true);
      },
    });

  const [getEmployeeById, { loading: searchingId }] =
    useLazyQuery<QueryByIdResult>(GET_EMPLOYEE_BY_ID, {
      onCompleted: (data) => {
        setSelectedEmployee(data.employee);
        setSubmited(true);
      },
    });

  // Select the resolution mode
  const resolutionMode: InputModes = React.useMemo(() => {
    if (mode) return mode;

    let checkMode: InputModes = 'autocomplete';
    if (
      user.modules?.includes('EMPLOYEE_REGISTRY_MODULE') ||
      user.modules?.includes('EMPLOYEE_BARCODE_MODULE')
    ) {
      checkMode = 'input';
    }
    return checkMode;
  }, [mode, user.modules]);

  /**
   * Resolves the alert creating a new alert step
   */
  const handleSubmitAction = React.useCallback(
    async (value?: string) => {
      if (!submited) {
        const submitedValue = employeeRegistry || value;
        if (!submitedValue) {
          setSubmited(true);
          return;
        }
        if (resolutionMode === 'autocomplete') {
          await getEmployeeById({ variables: { id: submitedValue } });
        } else {
          await getEmployeesByRegistry({
            variables: { registry: submitedValue },
          });
        }
        return;
      }

      onSubmit(
        selectedEmployee?.enabled
          ? selectedEmployee.id
          : user.employeeId || null,
      );
    },
    [
      employeeRegistry,
      getEmployeeById,
      getEmployeesByRegistry,
      onSubmit,
      resolutionMode,
      selectedEmployee,
      submited,
      user.employeeId,
    ],
  );

  const handleNoDevicesFound = () => {
    error('Nenhum dispositivo de leitura encontrado');
    setFallbackMode('input');
  };

  const handleEditClick = React.useCallback(() => {
    setSelectedEmployee(undefined);
    setSubmited(false);
    setEmployeeRegistry('');
  }, []);

  const parsedSubmitButtonLabel = React.useMemo(() => {
    if (submited) {
      return (
        submitButtonLabel ||
        (labelInfinitive
          ? `${capitalize(labelInfinitive)} solicitação`
          : 'Confirmar')
      );
    }
    return 'Avançar';
  }, [labelInfinitive, submitButtonLabel, submited]);

  const loading = React.useMemo(
    () => saving || searchingId || searchingRegistry,
    [saving, searchingId, searchingRegistry],
  );

  React.useEffect(() => {
    (async () => {
      if (canSkipIdentification && (employeeId || user.employeeId)) {
        await getEmployeeById({
          variables: { id: employeeId || user.employeeId },
        });
      }
    })();
  }, [employeeId, getEmployeeById, canSkipIdentification, user.employeeId]);

  return (
    <Modal
      id={id || 'employee-identification'}
      onHide={onHide}
      footer={
        <div
          className={classNames(
            'd-flex flex-justify-end flex-items-center gap-2',
          )}
        >
          {loading && (
            <div className="d-flex flex-justify-center p-2">
              <Spinner size="sm" />
            </div>
          )}

          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              onHide?.();
              onCancel?.();
            }}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            id="submit-button"
            type="button"
            size="lg"
            onClick={() => handleSubmitAction()}
            disabled={loading || isInvalid}
          >
            {parsedSubmitButtonLabel}
          </Button>
        </div>
      }
      {...modalProps}
    >
      <>
        {canSkipIdentification && (
          <div className="d-flex flex-column gap-3">
            <Text>{`Esta solicitação será ${
              labelPast || 'iniciada'
            } por:`}</Text>
            <Text weight="bold" id="employee-details">
              {selectedEmployee && selectedEmployee.enabled
                ? `${selectedEmployee.name} (${
                    selectedEmployee.registry?.hospitalRegistry ??
                    'Sem matrícula'
                  })`
                : `Anônimo`}
            </Text>
          </div>
        )}

        {!canSkipIdentification && (
          <form className="p-1" onSubmit={(e) => e.preventDefault()}>
            {submited ? (
              <div>
                <Text>
                  {labelInfinitive
                    ? `Por favor, confirme seus dados para ${labelInfinitive} esta solicitação:`
                    : `Por favor, confirme seus dados:`}
                </Text>

                <div className="d-flex flex-items-center mt-3">
                  <Text weight="bold" id="employee-details">
                    {selectedEmployee && selectedEmployee.enabled
                      ? `${selectedEmployee.name} (${
                          selectedEmployee.registry?.hospitalRegistry ??
                          'Sem matrícula'
                        })`
                      : `Anônimo`}
                  </Text>
                  <Button
                    id="change-employee-button"
                    className="ml-2"
                    variant="link"
                    onClick={handleEditClick}
                  >
                    <Icon icon={faPen} />
                  </Button>
                </div>

                {!selectedEmployee && (
                  <Text size="sm2" color="red500">
                    <>
                      {hospitalConfig?.orderSolutionKey?.mode === 'cpf'
                        ? 'CPF não encontrado. '
                        : 'Matrícula não encontrada. '}
                      {isInvalid &&
                        `Verifique os dados inseridos e tente novamente.`}
                    </>
                  </Text>
                )}

                {selectedEmployee && !selectedEmployee.enabled && (
                  <Text size="sm2" color="red500">
                    Seu cadastro encontra-se inativo, contate um administrador
                    para ativar seu cadastro.
                  </Text>
                )}
              </div>
            ) : (
              <SelectionHandler
                onChange={(newValue) => setEmployeeRegistry(newValue)}
                onSubmitValue={handleSubmitAction}
                onNoDevicesFound={() => handleNoDevicesFound()}
                mode={fallbackMode ?? resolutionMode}
                orderSolutionKey={hospitalConfig?.orderSolutionKey}
              />
            )}
          </form>
        )}
      </>
    </Modal>
  );
};

export default EmployeeIdContainer;
